import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import transactionService from '../services/transactionService';
import categoryService from '../services/categoryService';

const BulkEditModal = ({ 
  isOpen, 
  onClose, 
  selectedTransactionIds = [], 
  onSuccess,
  categories = [],
  user
}) => {
  const [loading, setLoading] = useState(false);
  const [bulkEdits, setBulkEdits] = useState({
    ownerType: '',
    type: '',
    category: '',
    isShared: '',
    notes: '',
    isRecurring: ''
  });

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setBulkEdits(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Build update object with only non-empty fields
      const updates = {};
      if (bulkEdits.ownerType) updates.editableOwner = bulkEdits.ownerType; // Update editableOwner, not ownerType
      if (bulkEdits.type) updates.type = bulkEdits.type;
      if (bulkEdits.category) updates.categoryRef = bulkEdits.category;
      if (bulkEdits.isShared !== '') updates.isShared = bulkEdits.isShared === 'true';
      if (bulkEdits.notes) updates.notes = bulkEdits.notes;
      if (bulkEdits.isRecurring !== '') updates.isRecurring = bulkEdits.isRecurring === 'true';

      if (Object.keys(updates).length === 0) {
        toast.error('Please select at least one field to update');
        return;
      }

      // Update all selected transactions
      const promises = selectedTransactionIds.map(id =>
        transactionService.updateTransaction(id, updates)
      );

      await Promise.all(promises);

      toast.success(`Successfully updated ${selectedTransactionIds.length} transactions`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error bulk updating transactions:', error);
      toast.error('Failed to update transactions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900" id="modal-title">
                Bulk Edit Transactions
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Editing {selectedTransactionIds.length} selected transaction{selectedTransactionIds.length > 1 ? 's' : ''}.
              Only fields you modify will be updated.
            </p>

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Owner Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Owner
                </label>
                <select
                  value={bulkEdits.ownerType}
                  onChange={(e) => handleChange('ownerType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- No Change --</option>
                  <option value="personal">Personal ({user?.firstName || 'Me'})</option>
                  {user?.partner && (
                    <option value="partner">Partner ({user.partner.firstName})</option>
                  )}
                  {user?.createdPartner && !user?.partner && (
                    <option value="partner">Partner ({user.createdPartner.firstName})</option>
                  )}
                  <option value="joint">Joint</option>
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={bulkEdits.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- No Change --</option>
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                  <option value="transfer">Transfer</option>
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={bulkEdits.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- No Change --</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Shared */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shared
                </label>
                <select
                  value={bulkEdits.isShared}
                  onChange={(e) => handleChange('isShared', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- No Change --</option>
                  <option value="true">Yes (Shared)</option>
                  <option value="false">No (Not Shared)</option>
                </select>
              </div>

              {/* Recurring */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recurring
                </label>
                <select
                  value={bulkEdits.isRecurring}
                  onChange={(e) => handleChange('isRecurring', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- No Change --</option>
                  <option value="true">Yes (Recurring)</option>
                  <option value="false">No (Not Recurring)</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={bulkEdits.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Add notes to all selected transactions..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : `Update ${selectedTransactionIds.length} Transaction${selectedTransactionIds.length > 1 ? 's' : ''}`}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkEditModal;

